import React, { useEffect, useMemo, useState } from 'react';
import {
    Badge,
    Box,
    Card,
    Group,
    NumberInput,
    SegmentedControl,
    SimpleGrid,
    Stack,
    Text,
    UnstyledButton,
} from '@mantine/core';
import type { VenuePricingParams } from '../../pricing/venuePricing';
import {
    BOUND_OUTCOME_META,
    BOUNDABLE_OUTCOMES,
    CUSTOMER_CASH_HORIZON_MONTHS,
    SWEEP_OUTCOME_META,
    boundMetricValue,
    computeCustomerCashSeries,
    failedBounds,
    hasActiveBounds,
    outcomeValue,
    pointHonorsBounds,
    type BoundableOutcome,
    type FeasibleInputSet,
    type OutputBounds,
    type SweepableVenueParam,
    type SweepParamUnit,
    type VenueParamSweep,
    type VenueSweepMetrics,
    type VenueSweepOutcome,
} from '../../pricing/venuePricingSweep';
import {
    CustomerCashTrajectoryChart,
    SensitivityDetailChart,
    SensitivitySparkline,
} from './VenueSensitivityPlots';

const OUTCOMES: VenueSweepOutcome[] = [
    'upFrontCapital',
    'breakEvenMonths',
    'netProfitYear1',
    'netProfitYear2',
    'customerUpFront',
    'customerMrr',
];

const currency = (n: number) =>
    n.toLocaleString(undefined, { style: 'currency', currency: 'USD', maximumFractionDigits: 0 });

const currencyDetail = (n: number) =>
    n.toLocaleString(undefined, {
        style: 'currency',
        currency: 'USD',
        minimumFractionDigits: 0,
        maximumFractionDigits: 2,
    });

function formatInput(value: number, unit: SweepParamUnit): string {
    if (unit === 'pct') return `${Math.round(value * 1000) / 10}%`;
    if (unit === 'count') return String(Math.round(value));
    if (unit === 'days') return `${Math.round(value)} d`;
    return currency(value);
}

function formatOutcome(value: number | null, outcome: VenueSweepOutcome): string {
    if (value == null) return 'Never';
    if (outcome === 'breakEvenMonths') {
        if (value === 0) return 'At conversion';
        return `${Math.round(value)} mo`;
    }
    return currency(value);
}

function formatBoundCurrent(value: number | null, unit: 'usd' | 'months'): string {
    if (value == null) return '—';
    if (unit === 'months') {
        if (value === 0) return 'At conversion';
        return `${Math.round(value)} mo`;
    }
    return currency(value);
}

function spanLabel(sweep: VenueParamSweep, outcome: VenueSweepOutcome): string {
    const ys = sweep.points
        .map(p => outcomeValue(p, outcome))
        .filter((y): y is number => y != null);
    if (ys.length === 0) return 'Never in range';
    const min = Math.min(...ys);
    const max = Math.max(...ys);
    if (min === max) return `Flat · ${formatOutcome(min, outcome)}`;
    return `${formatOutcome(min, outcome)} → ${formatOutcome(max, outcome)}`;
}

function BoundPair({
    outcome,
    bounds,
    current,
    onChange,
}: {
    outcome: BoundableOutcome;
    bounds: OutputBounds;
    current: number | null;
    onChange: (next: OutputBounds) => void;
}) {
    const meta = BOUND_OUTCOME_META[outcome];
    const range = bounds[outcome];
    const isUsd = meta.unit === 'usd';

    const patchSide = (side: 'min' | 'max', v: number | string) => {
        const n = typeof v === 'number' && Number.isFinite(v) ? v : undefined;
        const nextRange = { ...bounds[outcome], [side]: n };
        if (nextRange.min == null && nextRange.max == null) {
            const rest = { ...bounds };
            delete rest[outcome];
            onChange(rest);
            return;
        }
        onChange({ ...bounds, [outcome]: nextRange });
    };

    return (
        <Box>
            <Group justify="space-between" gap="xs" mb={4} wrap="nowrap">
                <Text size="sm" fw={500}>
                    {meta.label}
                </Text>
                <Text size="xs" c="dimmed">
                    Now {formatBoundCurrent(current, meta.unit)}
                </Text>
            </Group>
            <Group gap="xs" grow>
                <NumberInput
                    size="xs"
                    placeholder="Min"
                    hideControls
                    prefix={isUsd ? '$' : undefined}
                    suffix={isUsd ? undefined : ' mo'}
                    thousandSeparator={isUsd ? ',' : undefined}
                    value={range?.min ?? ''}
                    onChange={v => patchSide('min', v)}
                />
                <NumberInput
                    size="xs"
                    placeholder="Max"
                    hideControls
                    prefix={isUsd ? '$' : undefined}
                    suffix={isUsd ? undefined : ' mo'}
                    thousandSeparator={isUsd ? ',' : undefined}
                    value={range?.max ?? ''}
                    onChange={v => patchSide('max', v)}
                />
            </Group>
        </Box>
    );
}

type Props = {
    params: VenuePricingParams;
    mrr: number;
    patch: <K extends keyof VenuePricingParams>(key: K, value: VenuePricingParams[K]) => void;
    bounds: OutputBounds;
    onBoundsChange: (bounds: OutputBounds) => void;
    sweeps: VenueParamSweep[];
    currentMetrics: VenueSweepMetrics;
    feasibleByKey: Partial<Record<SweepableVenueParam, FeasibleInputSet>>;
};

const VenueSensitivityExplorer: React.FC<Props> = ({
    params,
    mrr,
    patch,
    bounds,
    onBoundsChange,
    sweeps,
    currentMetrics,
    feasibleByKey,
}) => {
    const [outcome, setOutcome] = useState<VenueSweepOutcome>('upFrontCapital');
    const [selectedKey, setSelectedKey] = useState<SweepableVenueParam>('deviceHardwareCost');

    useEffect(() => {
        if (!sweeps.some(s => s.key === selectedKey) && sweeps[0]) {
            setSelectedKey(sweeps[0].key);
        }
    }, [sweeps, selectedKey]);

    const selected = sweeps.find(s => s.key === selectedKey) ?? sweeps[0];
    const selectedFeasible = selected ? feasibleByKey[selected.key] : undefined;

    const customerSeries = useMemo(
        () => computeCustomerCashSeries(params, CUSTOMER_CASH_HORIZON_MONTHS),
        [params]
    );

    const conversionMonth = params.pilotDurationDays <= 0 ? 0 : Math.ceil(params.pilotDurationDays / 30);
    const isSteadyState = params.pilotDurationDays <= 0;
    const end = customerSeries[customerSeries.length - 1];

    const active = hasActiveBounds(bounds);
    const failing = failedBounds(currentMetrics, bounds);
    const boundKeys = BOUNDABLE_OUTCOMES.filter(k => {
        if (k === 'customerConversion' && isSteadyState) return false;
        return true;
    });
    const activeCount = boundKeys.filter(k => {
        const r = bounds[k];
        return r && (r.min != null || r.max != null);
    }).length;
    const quoteFeasible = !active || failing.length === 0;

    const pickInput = (key: SweepableVenueParam, unit: SweepParamUnit, x: number) => {
        const sweep = sweeps.find(s => s.key === key);
        if (sweep && active) {
            const nearest = sweep.points.reduce((best, p) =>
                Math.abs(p.inputValue - x) < Math.abs(best.inputValue - x) ? p : best
            );
            if (!pointHonorsBounds(nearest, bounds)) return;
        }
        if (key === 'goForwardDeviceCount') {
            patch(key, Math.max(params.pilotDeviceCount, Math.round(x)));
            return;
        }
        if (unit === 'pct') {
            patch(key, Math.min(1, Math.max(0, x)) as VenuePricingParams[typeof key]);
            return;
        }
        if (unit === 'count' || unit === 'days') {
            patch(key, Math.round(x) as VenuePricingParams[typeof key]);
            return;
        }
        patch(key, x as VenuePricingParams[typeof key]);
    };

    const customerBounds = boundKeys.filter(k => BOUND_OUTCOME_META[k].group === 'customer');
    const watchlyBounds = boundKeys.filter(k => BOUND_OUTCOME_META[k].group === 'watchly');

    return (
        <Stack gap="xl">
            <Card withBorder padding="lg" radius="md" shadow="sm">
                <Group justify="space-between" mb="xs" wrap="wrap" gap="xs">
                    <Text fw={600}>Goals</Text>
                    {active ? (
                        <Badge variant="light" color={quoteFeasible ? 'teal' : 'yellow'}>
                            {activeCount - failing.length} of {activeCount} bounds satisfied
                        </Badge>
                    ) : (
                        <Badge variant="light" color="gray">
                            No bounds set
                        </Badge>
                    )}
                </Group>
                <Text size="sm" c="dimmed" mb="md" maw={720}>
                    Optional min/max on customer invoice totals and Watchly P&L. Impact curves shade values
                    that miss a bound (other inputs held at this quote). A violating quote stays editable.
                </Text>
                {!quoteFeasible && (
                    <Text size="sm" c="yellow.8" mb="md">
                        This quote misses:{' '}
                        {failing.map(k => BOUND_OUTCOME_META[k].label).join(', ')}.
                    </Text>
                )}
                <SimpleGrid cols={{ base: 1, sm: 2 }} spacing="lg">
                    <Stack gap="sm">
                        <Text size="xs" c="dimmed" tt="uppercase" fw={600}>
                            Customer
                        </Text>
                        {customerBounds.map(k => (
                            <BoundPair
                                key={k}
                                outcome={k}
                                bounds={bounds}
                                current={boundMetricValue(currentMetrics, k)}
                                onChange={onBoundsChange}
                            />
                        ))}
                    </Stack>
                    <Stack gap="sm">
                        <Text size="xs" c="dimmed" tt="uppercase" fw={600}>
                            Watchly
                        </Text>
                        {watchlyBounds.map(k => (
                            <BoundPair
                                key={k}
                                outcome={k}
                                bounds={bounds}
                                current={boundMetricValue(currentMetrics, k)}
                                onChange={onBoundsChange}
                            />
                        ))}
                    </Stack>
                </SimpleGrid>
            </Card>

            <Card withBorder padding="lg" radius="md" shadow="sm">
                <Group justify="space-between" mb="xs" wrap="wrap" gap="xs">
                    <Text fw={600}>Impact curves</Text>
                    <Badge variant="light" color="indigo">
                        Other inputs held at this quote
                    </Badge>
                </Group>
                <Text size="sm" c="dimmed" mb="md" maw={720}>
                    Each sparkline varies one input across a fixed range. Click a cell to open full plots.
                    Click a feasible point on a full plot to apply that value to the quote above. Gray bands
                    are outside the goals.
                </Text>

                <SegmentedControl
                    fullWidth
                    size="xs"
                    value={outcome}
                    onChange={v => setOutcome(v as VenueSweepOutcome)}
                    data={OUTCOMES.map(id => ({
                        value: id,
                        label: SWEEP_OUTCOME_META[id].shortLabel,
                    }))}
                    mb="md"
                />

                <SimpleGrid cols={{ base: 1, sm: 2, md: 3 }} spacing="sm">
                    {sweeps.map(sweep => {
                        const isSelected = sweep.key === selected?.key;
                        const points = sweep.points.map(p => ({
                            x: p.inputValue,
                            y: outcomeValue(p, outcome),
                        }));
                        const feas = feasibleByKey[sweep.key];
                        return (
                            <UnstyledButton
                                key={sweep.key}
                                onClick={() => setSelectedKey(sweep.key)}
                                style={{ width: '100%' }}
                            >
                                <Card
                                    withBorder
                                    padding="sm"
                                    radius="md"
                                    shadow={isSelected ? 'sm' : undefined}
                                    style={{
                                        borderColor: isSelected
                                            ? 'var(--mantine-color-indigo-5)'
                                            : undefined,
                                    }}
                                >
                                    <Text size="sm" fw={600} lineClamp={2}>
                                        {sweep.label}
                                    </Text>
                                    <Text size="xs" c="dimmed" mb={4}>
                                        Now {formatInput(sweep.current, sweep.unit)} · {spanLabel(sweep, outcome)}
                                    </Text>
                                    <SensitivitySparkline
                                        points={points}
                                        currentX={sweep.current}
                                        selected={isSelected}
                                        currentInfeasible={active && !quoteFeasible}
                                        infeasibleRanges={feas?.infeasibleRanges}
                                        ariaLabel={`${sweep.label} vs ${SWEEP_OUTCOME_META[outcome].label}`}
                                    />
                                </Card>
                            </UnstyledButton>
                        );
                    })}
                </SimpleGrid>

                {selected && (
                    <Box mt="xl">
                        <Group justify="space-between" mb="sm" wrap="wrap">
                            <Text fw={600}>{selected.label}</Text>
                            <Text size="sm" c="dimmed">
                                Current {formatInput(selected.current, selected.unit)}
                                {selectedFeasible?.kind === 'empty'
                                    ? ' · no value in range satisfies the goals'
                                    : selectedFeasible?.kind === 'union'
                                      ? ' · feasible in disconnected ranges'
                                      : ''}
                            </Text>
                        </Group>
                        <SimpleGrid cols={{ base: 1, md: 2 }} spacing="lg">
                            {OUTCOMES.map(id => {
                                const meta = SWEEP_OUTCOME_META[id];
                                const points = selected.points.map(p => ({
                                    x: p.inputValue,
                                    y: outcomeValue(p, id),
                                }));
                                return (
                                    <Box key={id}>
                                        <Text size="sm" fw={500} mb={4}>
                                            {meta.label}
                                        </Text>
                                        <SensitivityDetailChart
                                            points={points}
                                            currentX={selected.current}
                                            currentInfeasible={active && !quoteFeasible}
                                            infeasibleRanges={selectedFeasible?.infeasibleRanges}
                                            xLabel={selected.label}
                                            yLabel={meta.label}
                                            formatX={n => formatInput(n, selected.unit)}
                                            formatY={n => formatOutcome(n, id)}
                                            yIsCurrency={meta.unit === 'usd'}
                                            onPickX={x => pickInput(selected.key, selected.unit, x)}
                                            pickAllowed={x => {
                                                if (!active) return true;
                                                const nearest = selected.points.reduce((best, p) =>
                                                    Math.abs(p.inputValue - x) < Math.abs(best.inputValue - x)
                                                        ? p
                                                        : best
                                                );
                                                return pointHonorsBounds(nearest, bounds);
                                            }}
                                        />
                                    </Box>
                                );
                            })}
                        </SimpleGrid>
                    </Box>
                )}
            </Card>

            <Card withBorder padding="lg" radius="md" shadow="sm">
                <Group justify="space-between" mb="xs" wrap="wrap" gap="xs">
                    <Text fw={600}>Customer cost trajectory</Text>
                    <Badge variant="light" color="teal">
                        {CUSTOMER_CASH_HORIZON_MONTHS / 12} years
                    </Badge>
                </Group>
                <Text size="sm" c="dimmed" mb="md" maw={720}>
                    Cumulative cash the venue pays from this quote: one-time (package, hardware purchase,
                    install trips) versus subscription (lease and software, including the first period on
                    the signup or conversion invoice).
                </Text>
                <SimpleGrid cols={{ base: 1, sm: 2 }} spacing="md" mb="lg">
                    <Box>
                        <Text size="xs" c="dimmed" tt="uppercase" fw={600}>
                            One-time through conversion
                        </Text>
                        <Text fw={700} size="lg">
                            {currency(end?.cumulativeOneTime ?? 0)}
                        </Text>
                        <Text size="xs" c="dimmed">
                            Package, hardware purchase after credit, and install-trip fees.
                        </Text>
                    </Box>
                    <Box>
                        <Text size="xs" c="dimmed" tt="uppercase" fw={600}>
                            Recurring after conversion
                        </Text>
                        <Text fw={700} size="lg">
                            {currencyDetail(mrr)}/mo
                        </Text>
                        <Text size="xs" c="dimmed">
                            {params.softwareCadence === 'year'
                                ? 'Monthly equivalent. Annual software is billed as a lump sum on the anniversary of conversion.'
                                : 'Discounted lease (if leasing) plus software at go-forward count.'}
                        </Text>
                    </Box>
                </SimpleGrid>
                <CustomerCashTrajectoryChart data={customerSeries} conversionMonth={conversionMonth} />
                <Text size="xs" c="dimmed" mt="md">
                    {isSteadyState
                        ? `Signup is month 0 (install and purchase are one-time; first software/lease period is subscription). End of year two: ${currency(end?.cumulativeTotal ?? 0)}.`
                        : `Signup is month 0; conversion lands at month ${conversionMonth}. End of year two: ${currency(end?.cumulativeTotal ?? 0)}.`}
                </Text>
            </Card>
        </Stack>
    );
};

export default VenueSensitivityExplorer;
